// TODO: should show validation error on focus (when empty)
// TODO: add tests

'use client'

import classNames from 'classnames'
import { useState } from 'react'
import type { ChangeEvent, FC } from 'react'
import styles from './PasswordInput.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { PasswordField } from '@/tools/ui'
import {
  getInputErrorMessage,
  getInputInfoMessage,
} from '@/tools/ui/components/password-input/helpers'
import { PasswordStrength } from '@/user/constants'
import type { UserPassword } from '@/user/entities'
import { getPasswordStrength, isStrongEnoughPassword } from '@/user/use-cases'

export interface PasswordInputChangeEvent {
  isValid: boolean
  password: string
}

interface Props {
  disableHint?: boolean
  validationError: string | null
  onChange: (event: PasswordInputChangeEvent) => void
  label?: string
  error?: string
  value?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const PasswordInput: FC<Props> = ({
  disableHint,
  validationError,
  onChange,
  error,
  label,
  onKeyDown: handleKeyDown,
}) => {
  const { t } = useLocalisation()

  const [password, setPassword] = useState<UserPassword>('')
  const [isFieldFocused, setIsFieldFocused] = useState<boolean>(false)
  const passwordStrength: PasswordStrength | null = getPasswordStrength(password)
  const inputErrorMessage = getInputErrorMessage(validationError, passwordStrength, t)
  const inputInfoMessage = disableHint
    ? ''
    : getInputInfoMessage(validationError, passwordStrength, t)

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setPassword(inputValue)
    setIsFieldFocused(true)
    onChange({
      isValid: Boolean(isStrongEnoughPassword(inputValue) && !validationError),
      password: inputValue,
    })
  }

  const handleFocus = () => {
    setIsFieldFocused(true)
  }

  const handleOnBlur = () => {
    setIsFieldFocused(false)
  }

  const inputMessageClass = classNames({
    [styles.inputValidation]: true,
    [styles.info]: passwordStrength === PasswordStrength.SHORT_PASSWORD,
    [styles.success]: isStrongEnoughPassword(password) && !validationError,
  })

  return (
    <div className={styles.passwordField}>
      <PasswordField
        aria-label={label || t('common.password')}
        error={inputErrorMessage || error}
        hidden={true}
        label={label || t('common.password')}
        value={password}
        onBlur={handleOnBlur}
        onChange={handlePasswordChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      {isFieldFocused && <div className={inputMessageClass}>{inputInfoMessage}</div>}
    </div>
  )
}
